import Vue from 'vue'
import { Store } from 'vuex'
import { Actions, Context, Getters, Module, Mutations } from 'vuex-smart-module'
import { ContextPosition } from 'vuex-smart-module/lib/context'

interface ExtendedContext
  extends Omit<Context<Module<LoadingState, undefined, LoadingMutations, LoadingActions>>, 'pos'> {
  readonly pos: ContextPosition
}

export class LoadingState<A extends Omit<Actions, '$init'> = LoadingActions> {
  loaders: {
    [K in keyof A]?: boolean
  } = {}
}

class LoadingMutations extends Mutations<LoadingState> {
  loading({ path, loading }: { path: string; loading: boolean }) {
    const action = path.split('/').pop()
    Vue.set(this.state.loaders, action, loading)
  }
}

class LoadingActions extends Actions<LoadingState, undefined, LoadingMutations, LoadingActions> {
  __ctx__!: ExtendedContext

  $init(store: Store<unknown>): void {
    const path = this.__ctx__.pos.namespace

    store.subscribeAction({
      before: action => {
        if (action.type.includes(path)) this.mutations.loading({ path: action.type, loading: true })
      },
      after: action => {
        if (action.type.includes(path)) this.mutations.loading({ path: action.type, loading: false })
      },
      error: action => {
        if (action.type.includes(path)) this.mutations.loading({ path: action.type, loading: false })
      },
    })
  }
}

export const loading = new Module({
  namespaced: false,
  state: LoadingState,
  mutations: LoadingMutations,
  actions: LoadingActions,
})

export class LoadingGetters<A extends Actions, S = Record<string, never>> extends Getters<
  S & { loading: LoadingState<Omit<A, '$init'>> }
> {
  loadingModule!: Context<typeof loading>

  get isLoading() {
    return Object.values(this.state.loading.loaders).includes(true)
  }

  $init(store: Store<unknown>) {
    this.loadingModule = loading.context(store)
  }
}
